import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Space, Popconfirm, message, Tag, Upload, List, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined, PaperClipOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../api'
import useStore from '../store'

function fmtAmt(v, currency) {
  if (v == null) return ''
  return `${parseFloat(v).toLocaleString('zh-CN')} ${currency}`
}

export default function IncomePage() {
  const [data, setData] = useState([])
  const [projects, setProjects] = useState([])
  const [accounts, setAccounts] = useState([])
  const [categories, setCategories] = useState([])
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [rateOptions, setRateOptions] = useState([])
  const [convertedAmt, setConvertedAmt] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [sort, setSort] = useState('created_desc')
  const [form] = Form.useForm()
  const { user } = useStore()
  const canEdit = user?.role !== 'readonly'

  const load = () => api.get(`/income?sort=${sort}`).then(setData)

  const loadAttachments = (id) => {
    if (id) api.get(`/attachments/income/${id}`).then(setAttachments)
    else setAttachments([])
  }

  useEffect(() => {
    load()
    api.get('/projects').then(setProjects)
    api.get('/accounts').then(setAccounts)
    api.get('/categories?type=income').then(setCategories)
  }, [sort])

  const fetchRateOptions = async (month, currency) => {
    if (!month) return
    const cur = currency || form.getFieldValue('currency')
    const pair = cur === 'USD' ? 'USD/VND' : 'RMB/VND'
    if (cur === 'VND') { setRateOptions([]); return }
    const [effective, base] = await Promise.all([
      api.get(`/exchange-rates/effective?year_month=${month}&currency_pair=${encodeURIComponent(pair)}`),
      api.get(`/exchange-rates/base?currency_pair=${encodeURIComponent(pair)}`)
    ])
    const opts = []
    if (effective.source === 'monthly') {
      opts.push({ label: `当月汇率: ${effective.rate}`, value: effective.rate, source: 'monthly' })
    }
    if (base.rate != null) {
      opts.push({ label: `基础汇率: ${base.rate}`, value: base.rate, source: 'base' })
    }
    setRateOptions(opts)
    if (opts.length > 0) {
      form.setFieldsValue({ exchange_rate: opts[0].value })
      recalcConverted(form.getFieldValue('amount'), cur, opts[0].value)
    }
  }

  const recalcConverted = (amount, currency, rate) => {
    if (!amount || !rate) { setConvertedAmt(null); return }
    const r = parseFloat(rate)
    if (currency === 'RMB') {
      setConvertedAmt({ value: amount * r, currency: 'VND' })
    } else if (currency === 'USD') {
      setConvertedAmt({ value: amount * r, currency: 'VND' })
    } else if (currency === 'VND') {
      setConvertedAmt(null)
    } else {
      setConvertedAmt(null)
    }
  }

  const onAmountOrCurrencyChange = () => {
    const { amount, currency, exchange_rate } = form.getFieldsValue(['amount', 'currency', 'exchange_rate'])
    const month = form.getFieldValue('account_month')
    // 切换币种时重新拉对应汇率
    fetchRateOptions(month, currency)
    recalcConverted(amount, currency, exchange_rate)
  }

  const openModal = (record = null) => {
    setEditing(record)
    setRateOptions([])
    setConvertedAmt(null)
    setPendingFiles([])
    loadAttachments(record?.id)
    if (record) {
      form.setFieldsValue({ ...record, actual_date: dayjs(record.actual_date) })
      fetchRateOptions(record.account_month, record.currency)
    } else {
      const thisMonth = dayjs().format('YYYY-MM')
      form.resetFields()
      form.setFieldsValue({ account_month: thisMonth })
      fetchRateOptions(thisMonth, 'RMB')
    }
    setOpen(true)
  }

  const onMonthChange = (e) => {
    const month = e.target.value
    if (/^\d{4}-\d{2}$/.test(month)) fetchRateOptions(month, form.getFieldValue('currency'))
  }

  const uploadFiles = async (recordId, files) => {
    for (const file of files) {
      const fd = new FormData()
      fd.append('file', file)
      await fetch(`/api/attachments/income/${recordId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      })
    }
  }

  const onSave = async () => {
    const values = await form.validateFields()
    const payload = { ...values, actual_date: values.actual_date.format('YYYY-MM-DD') }
    try {
      if (editing) {
        await api.put(`/income/${editing.id}`, payload)
        if (pendingFiles.length > 0) await uploadFiles(editing.id, pendingFiles)
      } else {
        const created = await api.post('/income', payload)
        if (pendingFiles.length > 0) await uploadFiles(created.id, pendingFiles)
      }
      message.success('保存成功')
      setOpen(false)
      load()
    } catch (err) { message.error(err) }
  }

  const openFile = async (url, filename) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
    if (!res.ok) { message.error('文件打开失败'); return }
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.target = '_blank'
    if (filename) a.download = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
  }

  const importExcel = async (file) => {
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch('/api/import/income', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      })
      const data = await res.json()
      if (!res.ok) { message.error(data.error || '导入失败'); return }
      const msg = data.skipped > 0
        ? `成功导入 ${data.imported} 条，跳过 ${data.skipped} 条（无项目）`
        : `成功导入 ${data.imported} 条记录`
      message.success(msg)
      load()
    } catch { message.error('导入失败') }
    return false
  }

  const columns = [
    { title: '名称', dataIndex: 'name', width: 150 },
    { title: '金额', dataIndex: 'amount', render: (v, r) => fmtAmt(v, r.currency) },
    { title: '所属月份', dataIndex: 'account_month', width: 100 },
    { title: '实际日期', dataIndex: 'actual_date', render: v => dayjs(v).format('YYYY-MM-DD'), width: 110 },
    { title: '类别', dataIndex: 'category_name', width: 100 },
    { title: '账户', dataIndex: 'account_name', width: 100 },
    { title: '项目', dataIndex: 'project_name', width: 120 },
    { title: '摘要', dataIndex: 'summary', ellipsis: true },
    { title: '添加人', dataIndex: 'created_by_name', width: 90 },
    { title: '修改人', dataIndex: 'updated_by_name', width: 90 },
    canEdit && {
      title: '操作', width: 100, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)} />
          <Popconfirm title="确认删除？" onConfirm={async () => { await api.delete(`/income/${r.id}`); load() }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ].filter(Boolean)

  const selectedSource = rateOptions.find(o => o.value === form.getFieldValue('exchange_rate'))?.source
  const selectedCurrency = form.getFieldValue('currency')
  const rateLabel = selectedCurrency === 'USD' ? '1 USD = ? VND' : '1 RMB = ? VND'

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>新增收入</Button>}
        {canEdit && (
          <Upload beforeUpload={importExcel} showUploadList={false} accept=".xlsx,.xls">
            <Button icon={<UploadOutlined />}>导入Excel</Button>
          </Upload>
        )}
        <Button icon={<DownloadOutlined />} onClick={() => openFile('/api/export/income', '收入记录.xlsx')}>导出Excel</Button>
        <Select value={sort} onChange={setSort} style={{ width: 140 }}>
          <Select.Option value="created_desc">按添加时间</Select.Option>
          <Select.Option value="amount_desc">金额从高到低</Select.Option>
          <Select.Option value="amount_asc">金额从低到高</Select.Option>
          <Select.Option value="date_desc">实际日期最新</Select.Option>
          <Select.Option value="date_asc">实际日期最早</Select.Option>
        </Select>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" scroll={{ x: 900 }} />
      <Modal title={editing ? '编辑收入' : '新增收入'} open={open} onOk={onSave} onCancel={() => setOpen(false)} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="amount" label="金额" rules={[{ required: true }]}>
            <InputNumber
              style={{ width: '100%' }} min={0}
              formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              parser={v => v.replace(/,/g, '')}
              onChange={onAmountOrCurrencyChange}
            />
          </Form.Item>
          <Form.Item name="currency" label="币种" rules={[{ required: true }]}>
            <Select onChange={cur => {
              // 币种变化时，若已选账户币种不匹配则清空账户
              const accId = form.getFieldValue('account_id')
              const acc = accounts.find(a => a.id === accId)
              if (acc && acc.currency !== cur) form.setFieldValue('account_id', undefined)
              onAmountOrCurrencyChange()
            }}>
              <Select.Option value="RMB">人民币(RMB)</Select.Option>
              <Select.Option value="VND">越南盾(VND)</Select.Option>
              <Select.Option value="USD">美元(USD)</Select.Option>
            </Select>
          </Form.Item>
          {convertedAmt && (
            <div style={{ marginTop: -16, marginBottom: 12, color: '#888', fontSize: 13 }}>
              ≈ {convertedAmt.value.toLocaleString('zh-CN', { maximumFractionDigits: 2 })} {convertedAmt.currency}
            </div>
          )}
          <Form.Item name="account_month" label="所属月份 (YYYY-MM)" rules={[{ required: true }]}>
            <Input placeholder="2024-01" onChange={onMonthChange} />
          </Form.Item>
          <Form.Item
            name="exchange_rate"
            label={
              <span>
                汇率 ({rateLabel})
                {selectedSource && (
                  <Tag color={selectedSource === 'monthly' ? 'blue' : 'orange'} style={{ marginLeft: 8 }}>
                    {selectedSource === 'monthly' ? '当月汇率' : '基础汇率'}
                  </Tag>
                )}
              </span>
            }
          >
            <Select placeholder="选择汇率" disabled={rateOptions.length === 0} onChange={v => recalcConverted(form.getFieldValue('amount'), form.getFieldValue('currency'), v)}>
              {rateOptions.map(o => (
                <Select.Option key={o.source} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="actual_date" label="实际日期" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="category_id" label="类别">
            <Select allowClear>{categories.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="account_id" label="账户" rules={[{ required: true }]}>
            <Select onChange={accId => {
              const acc = accounts.find(a => a.id === accId)
              if (acc) {
                form.setFieldValue('currency', acc.currency)
                onAmountOrCurrencyChange()
              }
            }}>
              {(() => {
                const cur = form.getFieldValue('currency')
                const filtered = cur ? accounts.filter(a => a.currency === cur) : accounts
                return filtered.map(a => <Select.Option key={a.id} value={a.id}>{a.name}({a.currency})</Select.Option>)
              })()}
            </Select>
          </Form.Item>
          <Form.Item name="project_id" label="项目" rules={[{ required: true, message: '请选择关联项目' }]}>
            <Select>{projects.map(p => <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="summary" label="摘要"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="note" label="备注"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item label="附件凭证">
            <Upload
              beforeUpload={file => {
                if (editing) return true  // 编辑模式直接上传
                setPendingFiles(prev => [...prev, file])
                return false  // 新增模式暂存，保存时一起提交
              }}
              action={editing ? `/api/attachments/income/${editing.id}` : undefined}
              headers={{ Authorization: `Bearer ${localStorage.getItem('token')}` }}
              showUploadList={false}
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={({ file }) => {
                if (file.status === 'done') { message.success('上传成功'); loadAttachments(editing?.id) }
                else if (file.status === 'error') { message.error('上传失败') }
              }}
            >
              <Button icon={<UploadOutlined />}>上传附件</Button>
              <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>支持图片、PDF、Word、Excel，最大10MB</span>
            </Upload>
            {!editing && pendingFiles.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 6 }}
                dataSource={pendingFiles}
                renderItem={(file, idx) => (
                  <List.Item actions={[
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}
                      onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))} />
                  ]}>
                    <PaperClipOutlined style={{ marginRight: 6, color: '#1677ff' }} />
                    <Typography.Text ellipsis style={{ maxWidth: 200 }}>{file.name}</Typography.Text>
                    <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>{(file.size / 1024).toFixed(1)}KB</span>
                  </List.Item>
                )}
              />
            )}
            {editing && attachments.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 6 }}
                dataSource={attachments}
                renderItem={att => (
                  <List.Item
                    actions={[
                      <Button type="link" size="small" icon={<DownloadOutlined />}
                        onClick={() => openFile(`/api/attachments/file/${att.id}`, att.original_name)}>预览</Button>,
                      canEdit && <Popconfirm title="确认删除附件？" onConfirm={async () => {
                        await api.delete(`/attachments/${att.id}`)
                        loadAttachments(editing.id)
                      }}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ].filter(Boolean)}
                  >
                    <PaperClipOutlined style={{ marginRight: 6, color: '#1677ff' }} />
                    <Typography.Text ellipsis style={{ maxWidth: 200 }}>{att.original_name}</Typography.Text>
                    <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                      {att.file_size ? `${(att.file_size / 1024).toFixed(1)}KB` : ''}
                    </span>
                  </List.Item>
                )}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
